import BugIcon from '@precis-digital/kurama/icons/BugIcon';
import CircleDashedIcon from '@precis-digital/kurama/icons/CircleDashedIcon';
import IgnoredIcon from '@precis-digital/kurama/icons/IgnoredIcon';
import LightIcon from '@precis-digital/kurama/icons/LightIcon';
import LoadIcon from '@precis-digital/kurama/icons/LoadIcon';
import PresentationBoardIcon from '@precis-digital/kurama/icons/PresentationBoardIcon';
import { type BadgeColor } from '@precis-digital/kurama/Badge';
import { type Theme, type ThemeColor } from '@precis-digital/kurama/theme';
import type { RecommendationStatus } from './types';
import type { RecommendationTypes, SeverityTypes } from 'shared/assetsBucket/recommendations';
import type { ExtendedPlatforms, Platforms } from 'shared/api/accounts';
export const HEALTH_CHECKERS_V2_SOURCE_SERVICE = 'Health Checkers V2';
export const DATA_SOURCE_PLATFORM_TO_HC_PLATFORM_MAP: Partial<Record<Platforms, ExtendedPlatforms>> = {
  google: 'google_ads',
  microsoft_advertising: 'bing',
  linkedin: 'linkedin_ads'
};

/**
 * Health Checker platforms hidden from Recommendations, excluded both from the
 * Cirrus event feed and from the rules metadata served by the assets bucket.
 *
 * Legacy `linkedin` is deprecated. Recommendations use `linkedin_ads`, which
 * has a month of Health Checker data.
 */
export const EXCLUDED_HC_PLATFORMS: ExtendedPlatforms[] = ['linkedin'];
export const UNASSIGNED = 'UNASSIGNED';

/**
 * Severity colours for status bars, chips, and the occurrence-over-time chart.
 *
 * Uses two token families on purpose:
 * - `color` → `dataViz.semantic.*.fill.default` (saturated stroke / bar edge)
 * - `backgroundColor` → `background.semantic.*.tonal.soft.default` (opaque area fill)
 *
 * Stacked chart fills must be opaque — semi-transparent dataViz fills (e.g. `fill.disabled`)
 * composite over lower strokes and look muddy. Opaque soft fills overlay the stroke cleanly.
 *
 * DESIGNER-DECISION: Confirm mixing dataViz (lines) with background.semantic (fills) is
 * intentional for this surface, or whether a single token family should own both roles.
 */
export const getSeverity = (theme: Theme): Record<SeverityTypes, {
  label: string;
  color: ThemeColor;
  backgroundColor: ThemeColor;
  colorName: BadgeColor;
}> => ({
  critical: {
    label: 'Critical',
    color: theme.tokens.dataViz.semantic.error.border.default,
    backgroundColor: theme.tokens.dataViz.semantic.error.fill.default,
    colorName: 'error'
  },
  moderate: {
    label: 'Moderate',
    color: theme.tokens.dataViz.semantic.warning.border.default,
    backgroundColor: theme.tokens.dataViz.semantic.warning.fill.default,
    colorName: 'warning'
  },
  low: {
    label: 'Low',
    color: theme.tokens.dataViz.semantic.info.border.default,
    backgroundColor: theme.tokens.dataViz.semantic.info.fill.default,
    colorName: 'info'
  }
});
export const SORTING_SEVERITY_MAP: Record<SeverityTypes, number> = {
  critical: 3,
  moderate: 2,
  low: 1
};
export const getRecommendationTypes = (theme: Theme): Record<RecommendationTypes, {
  type: RecommendationTypes;
  description: 'recommendationTypeBugDescription' | 'recommendationTypePerformanceDescription' | 'recommendationTypeBestPracticesDescription';
  icon: React.ForwardRefExoticComponent<React.RefAttributes<SVGSVGElement>>;
  backgroundColor: string;
  color: string;
}> => ({
  errors: {
    type: 'errors',
    description: 'recommendationTypeBugDescription',
    icon: BugIcon,
    backgroundColor: theme.tokens.dataViz.secondary[5].fill.default,
    color: theme.tokens.foreground.main.staticWhite
  },
  performance: {
    type: 'performance',
    description: 'recommendationTypePerformanceDescription',
    icon: PresentationBoardIcon,
    backgroundColor: theme.tokens.dataViz.secondary[3].fill.default,
    color: theme.tokens.foreground.main.staticWhite
  },
  best_practices: {
    type: 'best_practices',
    description: 'recommendationTypeBestPracticesDescription',
    icon: LightIcon,
    backgroundColor: theme.tokens.dataViz.secondary[4].fill.default,
    color: theme.tokens.foreground.main.staticWhite
  }
});
export const STATUS = {
  UNRESOLVED: {
    label: 'Unresolved',
    value: 'unresolved',
    icon: <CircleDashedIcon />
  },
  ONGOING: {
    label: 'Ongoing',
    value: 'ongoing',
    icon: <LoadIcon />
  },
  IGNORED: {
    label: 'Ignored',
    value: 'ignored',
    icon: <IgnoredIcon />
  }
} as const;
export const TABS = {
  overview: {
    id: 'overview',
    value: 1
  },
  howToFix: {
    id: 'howToFix',
    value: 2
  },
  viewAdvanced: {
    id: 'viewAdvanced',
    value: 1
  }
} as const;
export const IGNORED_FOR_DAYS = [30, 14, 7, 1, 0] as const;
export const ViewTypes = {
  active: 1,
  inactive: 2
} as const;
export const DATA_SOURCE_COLUMN_ID = '_source';
export const WRAP_STRING_COLUMNS_LENGTH_GREATER_THAN = 20;
export const WRAP_STRING_COLUMNS_AT = '20em';
export const RECOMMENDATIONS_POPUP_QUERY_PARAMETER_KEY = 'recommendationDetails';
export const RECOMMENDATION_DETAILS_QUERY_PARAMETER_KEY = 'ruleId';
export const recommendationItemStatusNone: RecommendationStatus = {
  statusId: undefined,
  status: 'unresolved',
  currentStatus: 'unresolved',
  ignored: false,
  ongoing: false,
  ignoredUntil: undefined,
  ignoredUntilDate: undefined,
  assignee: undefined
};